import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ActionIcon, Badge, Button, Card, Group, Loader, ScrollArea, SimpleGrid, Skeleton, Stack, Text, ThemeIcon, TextInput,
} from '@mantine/core';
import {
  IconArrowLeft, IconArrowsRightLeft, IconBuilding, IconCheck, IconSearch, IconX,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/app/providers/AuthProvider';
import { changeTenant } from '@/features/auth/api';
import { canManageSubTenants } from '@/features/tenants/tenantAccess';
import { STORAGE_KEYS } from '@/shared/lib/constants';
import { fetchList } from '@/shared/api/jsonapi/client';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { PageHeader } from '@/shared/components/PageHeader';
import { EmptyState } from '@/shared/components/EmptyState';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/switch-tenant')({ component: SwitchTenantPage });

interface TenantRow extends Denormalized { type: 'tenants'; name?: string; businessName?: string }

function SwitchTenantPage() {
  const navigate = useNavigate();
  const {
    decoded, associatedTenants, refresh,
    crossTenantOverride, setCrossTenantOverride, clearCrossTenantOverride,
  } = useAuth();
  const currentTenantId = decoded?.tenantId;
  const canCross = canManageSubTenants(decoded as Record<string, unknown> | null);
  const isSuperAdmin = decoded?.superadmin === 'True';

  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query.trim(), 300);
  const [switching, setSwitching] = useState<string | null>(null);

  // ── Le tue aziende (switch reale) — filtro client-side ──
  const myTenants = useMemo(() => {
    const list = associatedTenants ?? [];
    if (!debounced) return list;
    const q = debounced.toLowerCase();
    return list.filter((t) => t.name.toLowerCase().includes(q));
  }, [associatedTenants, debounced]);

  // ── Altre aziende (cross-tenant) — ricerca server-side via `like` ──
  const crossQ = useQuery({
    queryKey: ['switch-tenant-cross', currentTenantId, isSuperAdmin, debounced],
    enabled: canCross && Boolean(currentTenantId),
    staleTime: 60_000,
    queryFn: async () => {
      const filter: Record<string, { eq?: string; like?: string }> = {};
      if (!isSuperAdmin && currentTenantId) filter['parentTenant.id'] = { eq: currentTenantId };
      if (debounced) filter.name = { like: debounced };
      const res = await fetchList<TenantRow>('tenants', {
        filter: Object.keys(filter).length ? filter : undefined,
        sort: ['name'],
        page: { size: 100 },
      });
      return res.data.filter((t) => t.id !== currentTenantId);
    },
  });

  const onPickAssociated = async (tenantId: string, name: string) => {
    if (!tenantId || tenantId === currentTenantId) return;
    setSwitching(tenantId);
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.refreshToken);
      if (!stored) throw new Error('No refresh token');
      await changeTenant(tenantId, stored);
      await refresh();
      notifications.show({ color: 'green', message: `Azienda attiva: ${name}` });
      void navigate({ to: '/' });
    } catch (err) {
      const message = err instanceof JsonApiHttpError ? err.getUserMessage() : 'Cambio azienda fallito';
      notifications.show({ color: 'red', message });
    } finally {
      setSwitching(null);
    }
  };

  const onPickCross = (tenantId: string, name: string) => {
    if (tenantId === currentTenantId) return;
    setCrossTenantOverride(tenantId, name);
    notifications.show({ color: 'grape', message: `Stai operando su: ${name}` });
    void navigate({ to: '/' });
  };

  const hasMyTenants = (associatedTenants?.length ?? 0) > 1;
  const showsNothing = !hasMyTenants && !canCross;

  return (
    <Stack gap="lg">
      <PageHeader
        title="Cambia azienda"
        description="Cerca e seleziona l'azienda su cui vuoi lavorare."
        actions={
          <Button variant="default" leftSection={<IconArrowLeft size={16} />} onClick={() => void navigate({ to: '/' })}>
            Indietro
          </Button>
        }
      />

      {!showsNothing && (
        <TextInput
          size="md"
          placeholder="Cerca azienda per nome…"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          leftSection={<IconSearch size={18} />}
          rightSection={
            query ? (
              <ActionIcon variant="subtle" color="gray" aria-label="Pulisci ricerca" onClick={() => setQuery('')}>
                <IconX size={16} />
              </ActionIcon>
            ) : null
          }
          data-autofocus
        />
      )}

      {crossTenantOverride && (
        <Card withBorder padding="md" radius="md" bg="var(--mantine-color-grape-light)">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <ThemeIcon variant="light" color="grape" size={36} radius="md"><IconArrowsRightLeft size={20} /></ThemeIcon>
              <Text size="sm" lineClamp={1}>
                Stai operando in cross-tenant su un'altra azienda.
              </Text>
            </Group>
            <Button size="xs" variant="light" color="grape" onClick={() => { clearCrossTenantOverride(); void navigate({ to: '/' }); }}>
              Torna al tuo tenant
            </Button>
          </Group>
        </Card>
      )}

      {showsNothing && (
        <EmptyState
          icon={IconBuilding}
          title="Nessun'altra azienda disponibile"
          description="Il tuo account è associato a una sola azienda e non ha accesso cross-tenant."
        />
      )}

      {/* ── Le tue aziende ── */}
      {hasMyTenants && (
        <Card withBorder padding="lg" radius="md">
          <Group gap={6} mb="sm">
            <IconBuilding size={18} />
            <Text fw={600}>Le tue aziende</Text>
            <Badge variant="light" ml={4}>{myTenants.length}</Badge>
          </Group>
          {myTenants.length === 0 ? (
            <EmptyState title="Nessun risultato" description={`Nessuna azienda trovata per "${debounced}".`} py="md" />
          ) : (
            <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, lg: 4 }} spacing="md">
              {myTenants.map((t) => {
                const isCurrent = t.tenantId === currentTenantId;
                return (
                  <TenantCard
                    key={t.tenantId}
                    name={t.name}
                    color="blue"
                    badge={isCurrent ? { label: 'Attuale', color: 'blue' } : undefined}
                    current={isCurrent}
                    loading={switching === t.tenantId}
                    onClick={() => void onPickAssociated(t.tenantId, t.name)}
                  />
                );
              })}
            </SimpleGrid>
          )}
        </Card>
      )}

      {/* ── Altre aziende (cross-tenant) ── */}
      {canCross && (
        <Card withBorder padding="lg" radius="md">
          <Group gap={6} mb="sm">
            <IconArrowsRightLeft size={18} />
            <Text fw={600}>Altre aziende</Text>
            <Badge size="xs" color="grape" ml={4}>cross-tenant</Badge>
          </Group>

          {crossQ.isError ? (
            <QueryErrorAlert error={crossQ.error} onRetry={() => void crossQ.refetch()} />
          ) : crossQ.isLoading ? (
            <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, lg: 4 }} spacing="md">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} h={96} radius="md" />)}
            </SimpleGrid>
          ) : (crossQ.data?.length ?? 0) === 0 ? (
            <EmptyState
              icon={IconBuilding}
              title={debounced ? 'Nessun risultato' : 'Nessuna azienda'}
              description={debounced ? `Nessuna azienda trovata per "${debounced}".` : 'Non ci sono altre aziende su cui operare.'}
              py="md"
            />
          ) : (
            <ScrollArea.Autosize mah={520}>
              <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, lg: 4 }} spacing="md">
                {crossQ.data!.map((t) => {
                  const name = t.name ?? t.businessName ?? `Tenant ${t.id.slice(0, 8)}`;
                  const isActive = crossTenantOverride === t.id;
                  return (
                    <TenantCard
                      key={t.id}
                      name={name}
                      color="grape"
                      badge={isActive ? { label: 'Operando', color: 'grape' } : undefined}
                      onClick={() => onPickCross(t.id, name)}
                    />
                  );
                })}
              </SimpleGrid>
            </ScrollArea.Autosize>
          )}
          {!debounced && (crossQ.data?.length ?? 0) >= 100 && (
            <Text size="xs" c="dimmed" mt="xs">Mostrate le prime 100. Usa la ricerca per trovarne altre.</Text>
          )}
        </Card>
      )}
    </Stack>
  );
}

function TenantCard({ name, color, badge, current, loading, onClick }: {
  name: string;
  color: string;
  badge?: { label: string; color: string };
  current?: boolean;
  loading?: boolean;
  onClick: () => void;
}) {
  const interactive = !current && !loading;
  return (
    <Card
      withBorder
      radius="md"
      padding="md"
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        cursor: interactive ? 'pointer' : 'default',
        opacity: current ? 0.75 : 1,
        borderColor: current ? `var(--mantine-color-${color}-5)` : undefined,
      }}
    >
      <Stack gap="xs" align="center" ta="center">
        <ThemeIcon variant="light" color={color} size={48} radius="md">
          {loading ? <Loader size="sm" color={color} /> : <IconBuilding size={26} />}
        </ThemeIcon>
        <Text size="sm" fw={600} lineClamp={2} style={{ minHeight: '2.4em' }}>{name}</Text>
        {badge ? (
          <Badge color={badge.color} variant="light" leftSection={<IconCheck size={12} />}>{badge.label}</Badge>
        ) : (
          <Text size="xs" c="dimmed">Seleziona</Text>
        )}
      </Stack>
    </Card>
  );
}
